// The PLACEMENT-MOVE domain: the floor transform gizmo (translate arrows /
// center free-move / rotation rings + protractor), the marquee group's
// body-drag, and the footprint snapping both use to land objects flush against
// walls / other objects. Same shape as the other floor slices: this file owns
// the gesture state + bodies; floor-mode keeps the Select chain's order and
// calls the hooks at the original priority spots. Selection ORCHESTRATION
// (selectPlacement / context menu / model dims annotations) stays in floor-mode.

import * as THREE from "three";
import {
  AXIS_COLORS,
  RING_RADIUS,
  TransformGizmo,
  type GizmoHit,
} from "../../view/transform-gizmo";
import { RotationProtractor } from "../../view/rotation-protractor";
import { type PlacementOutline } from "../../view/placement-outline";
import { closestParamOnAxis } from "../../interaction/handlers/gizmo";
import { floorScene, expandRigidGroup, placementSystem, wallSystem } from "../core";
import { uiState } from "../../ui/state";
import { desktopMouse } from "../../input/desktop";
import { type FloorTool as FloorToolId } from "../../ui/floor-ui";
import { type MarqueeApi } from "./marquee";

const PLACE_SNAP = 0.12; // metres: footprint edge/center snap distance

export interface PlacementsDomainDeps {
  activeCamera: () => THREE.Camera;
  activeTool: () => FloorToolId;
  isShiftHeld: () => boolean;
  snapToGrid: (v: number) => number;
  angleSnapRad: number; // rotate snap (shared 15° step)
  marquee: MarqueeApi; // group selection rides on the marquee sets
  refreshSelectionUI: () => void;
  occluderDist: (ray: THREE.Raycaster) => number; // side-elevation pick barrier
  planViewAxis: () => 0 | 1 | 2; // which axis the 2D plan looks down
  freeMoveAxis: () => 0 | 1 | 2; // the free-move plane's normal axis
  placementOutline: PlacementOutline; // white-edge highlighter (floor-mode owns)
  // Extra ids to keep white-edged while the right-click menu is open.
  contextHighlightIds: () => readonly number[];
  viewScale: (p: THREE.Vector3) => number; // shared view-scale helper
}

export interface PlacementsDomainApi {
  floorGizmo: TransformGizmo;
  floorProtractor: RotationProtractor;
  gizmoPlaces: () => number[]; // the placements the gizmo acts on
  // Select-chain hooks.
  gizmoUnderCursor: () => boolean; // a press here must not clear the marquee
  beginGroupDrag: () => boolean; // press on an already-selected item → group move
  pressGizmo: () => boolean; // press on a gizmo handle (with front-object veto)
  dragGizmo: () => boolean; // claimed this frame?
  dragGroup: () => boolean;
  gizmoDragArmed: () => boolean;
  groupDragArmed: () => boolean;
  clearGestures: () => void; // bulk release / cancel
  // Per-frame: gizmo placement/visibility + selection/hover white-edge.
  updateGizmo: () => void;
}

export function installPlacementsDomain(deps: PlacementsDomainDeps): PlacementsDomainApi {
  const floorGizmo = new TransformGizmo();
  floorScene.root.add(floorGizmo.object3D);
  const floorProtractor = new RotationProtractor();
  floorScene.root.add(floorProtractor.object3D);

  const _ray = new THREE.Raycaster();
  const _fgAxes = [
    new THREE.Vector3(1, 0, 0),
    new THREE.Vector3(0, 1, 0),
    new THREE.Vector3(0, 0, 1),
  ];
  const _fgCenter = new THREE.Vector3();
  const _fgBox = new THREE.Box3();
  const _fgSize = new THREE.Vector3();
  // View-plane rotate scratch (same scheme as the modeling gizmo: a screen-facing
  // basis through the center, so any axis rotates predictably from any view).
  const _fgN = new THREE.Vector3();
  const _fgR = new THREE.Vector3();
  const _fgU = new THREE.Vector3();
  const _fgUp = new THREE.Vector3(0, 1, 0);
  const _fgCamPos = new THREE.Vector3();
  const _fgRingDir = new THREE.Vector3(); // center→camera dir for arc orientation
  const _fgHit = new THREE.Vector3();
  const _fgPlane = new THREE.Plane();
  const _fgDelta = new THREE.Quaternion();
  const _fgRel = new THREE.Vector3();
  const _fgCursorWorld = new THREE.Vector3();
  const _fgFreeHit = new THREE.Vector3(); // free-move: cursor hit on the drag plane
  const _fgMove = new THREE.Vector3(); // scratch: group translation delta
  let fgDrag: {
    mode: "translate" | "rotate" | "free";
    axis: number;
    grabParam: number; // translate: param along the axis at grab
    // Start transform of every selected placement (one for a single selection) —
    // the gizmo drives them all, pivoting on their shared centre.
    starts: { id: number; pos: THREE.Vector3; quat: THREE.Quaternion }[];
    placeSet: Set<number>; // the selected placement ids (excluded from snapping)
    // Combined footprint (world XZ) of the selected placements at grab time, used
    // to snap the whole group flush to walls / other objects as it's dragged.
    bbox: { minX: number; maxX: number; minZ: number; maxZ: number } | null;
    wallVids: number[]; // selected wall vertices to translate with the group
    applied: THREE.Vector3; // translate/free: last applied delta (for the wall step)
    grabPoint: THREE.Vector3; // free move: cursor hit on the drag plane at grab
    freeNormalAxis: number; // free move: plane normal (the view axis)
    center: THREE.Vector3; // grab center: axis line origin + rotate pivot
    prevAngle: number; // rotate: last view-plane angle
    accumAngle: number; // rotate: unwrapped sweep
    signFlip: number; // rotate: follow the cursor from either side of the axis
    refDir: THREE.Vector3; // rotate: grab dir in the ring plane (protractor start)
  } | null = null;
  // Group move of the marquee selection: total grid-snapped delta, like a wall
  // body-drag. grab/applied are full 3D so the marquee can move in the view
  // plane (X/Z aerial, X/Y or Y/Z from a side elevation). normalAxis = the view
  // axis held fixed. minPlaceY = lowest placement Y at grab time, so the
  // group's total descent is capped there (the whole selection stops together
  // when the lowest hits 0).
  let groupDrag: {
    grab: THREE.Vector3;
    normalAxis: 0 | 1 | 2;
    applied: THREE.Vector3;
    minPlaceY: number;
  } | null = null;

  // Cursor's hit on the "move plane" — perpendicular to `normalAxis` (the view
  // axis), through the origin. For an aerial/3D view (normal Y) this is the
  // floor; for a side elevation it's the vertical plane facing the camera.
  // (The plane's offset along its normal doesn't matter: ortho side views
  // project in parallel, and the aerial/3D case wants the y=0 floor, which
  // through-origin gives.)
  const _movePlane = new THREE.Plane();
  const _moveHit = new THREE.Vector3();
  const _moveOrigin = new THREE.Vector3(0, 0, 0);
  function viewPlanePointAtCursor(normalAxis: 0 | 1 | 2): THREE.Vector3 | null {
    _ray.setFromCamera(desktopMouse.ndc, deps.activeCamera());
    _movePlane.setFromNormalAndCoplanarPoint(_fgAxes[normalAxis], _moveOrigin);
    if (_ray.ray.intersectPlane(_movePlane, _moveHit)) return _moveHit;
    return null;
  }

  // ── Footprint snapping ────────────────────────────────────────────────────
  // Candidate X / Z snap coordinates: wall vertices (so a footprint abuts an
  // axis-aligned wall) plus every OTHER placement's footprint edges + centers.
  // Snap a placement's target floor center (x,z) to nearby walls / other
  // placements, independently in X and Z. Walls snap the object's NEAR edge flush
  // to a face it's OUTSIDE of (never the far face or centerline); other objects
  // align edges/centers on either side. Shift bypasses.
  // Snap a footprint — center (tcx,tcz), half-extents (hx,hz) — to nearby walls +
  // other placements, independently in X/Z. Returns the (dx,dz) to add to land
  // flush. `exclude` is the placement ids to ignore (the ones being moved), so a
  // group doesn't snap to its own members. Shift bypasses. Shared by single-object
  // moves AND group (gizmo / body) moves — so grouped objects snap like singles.
  function snapFootprint(
    tcx: number,
    tcz: number,
    hx: number,
    hz: number,
    exclude: ReadonlySet<number>,
  ): { dx: number; dz: number } {
    if (deps.isShiftHeld()) return { dx: 0, dz: 0 };
    let dx = 0;
    let dz = 0;
    let adx = PLACE_SNAP;
    let adz = PLACE_SNAP;
    const tryX = (target: number, edge: number): void => {
      const d = target - edge;
      if (Math.abs(d) < adx) {
        adx = Math.abs(d);
        dx = d;
      }
    };
    const tryZ = (target: number, edge: number): void => {
      const d = target - edge;
      if (Math.abs(d) < adz) {
        adz = Math.abs(d);
        dz = d;
      }
    };

    // Walls: snap only the edge facing the wall to the near face (the one the
    // object is already outside of), so it lands flush — never through to the
    // far face or onto the centerline. Axis-aligned faces only.
    for (const f of wallSystem.faceSegments()) {
      if (Math.abs(f.nz) < 1e-3 && Math.abs(f.nx) > 0.9) {
        const fx = f.ax; // vertical face
        if (f.nx > 0 && tcx > fx - PLACE_SNAP) tryX(fx, tcx - hx);
        else if (f.nx < 0 && tcx < fx + PLACE_SNAP) tryX(fx, tcx + hx);
      } else if (Math.abs(f.nx) < 1e-3 && Math.abs(f.nz) > 0.9) {
        const fz = f.az; // horizontal face
        if (f.nz > 0 && tcz > fz - PLACE_SNAP) tryZ(fz, tcz - hz);
        else if (f.nz < 0 && tcz < fz + PLACE_SNAP) tryZ(fz, tcz + hz);
      }
    }
    // Other placements: align edges or centers, either side.
    for (const oid of placementSystem.ids()) {
      if (exclude.has(oid)) continue;
      const o = placementSystem.aabbOf(oid);
      if (!o) continue;
      for (const t of [o.minX, o.maxX, (o.minX + o.maxX) / 2]) {
        tryX(t, tcx - hx);
        tryX(t, tcx + hx);
        tryX(t, tcx);
      }
      for (const t of [o.minZ, o.maxZ, (o.minZ + o.maxZ) / 2]) {
        tryZ(t, tcz - hz);
        tryZ(t, tcz + hz);
        tryZ(t, tcz);
      }
    }
    return { dx, dz };
  }
  // Snap a whole group's combined footprint after applying (deltaX, deltaZ).
  function snapGroup(
    box: { minX: number; maxX: number; minZ: number; maxZ: number },
    deltaX: number,
    deltaZ: number,
    members: ReadonlySet<number>,
    snapX: boolean,
    snapZ: boolean,
  ): { dx: number; dz: number } {
    const hx = (box.maxX - box.minX) / 2;
    const hz = (box.maxZ - box.minZ) / 2;
    const cx = (box.minX + box.maxX) / 2 + deltaX;
    const cz = (box.minZ + box.maxZ) / 2 + deltaZ;
    const s = snapFootprint(cx, cz, hx, hz, members);
    return { dx: snapX ? s.dx : 0, dz: snapZ ? s.dz : 0 };
  }

  // The placements the gizmo acts on: the marquee group if there is one, else
  // the single gizmo selection. The gizmo shows whenever this is non-empty.
  function gizmoPlaces(): number[] {
    if (deps.marquee.places().size > 0) return [...deps.marquee.places()];
    const sid = placementSystem.selectedId();
    return sid !== null ? [sid] : [];
  }

  // Set _fgCenter to the centre of the gizmo's placements — one object's bbox
  // centre, or the centroid of several (so the gizmo sits between them).
  function floorGizmoTarget(): boolean {
    const places = gizmoPlaces();
    if (places.length === 0) return false;
    _fgCenter.set(0, 0, 0);
    let n = 0;
    for (const pid of places) {
      const g = placementSystem.groupOf(pid);
      if (!g) continue;
      _fgBox.setFromObject(g);
      _fgBox.getCenter(_fgSize); // reuse _fgSize as scratch centre
      _fgCenter.add(_fgSize);
      n++;
    }
    if (n === 0) return false;
    _fgCenter.multiplyScalar(1 / n);
    return true;
  }

  const GIZMO_VIEW_SCALE = 0.06; // tune for the on-screen gizmo size
  function gizmoScreenScale(center: THREE.Vector3): number {
    return Math.max(0.05, deps.viewScale(center) * GIZMO_VIEW_SCALE);
  }

  function wrapPiF(a: number): number {
    while (a > Math.PI) a -= Math.PI * 2;
    while (a <= -Math.PI) a += Math.PI * 2;
    return a;
  }
  // Cursor's angle in the screen-facing plane through `center` (sets _fgN/_fgR/
  // _fgU). null if degenerate / the ray misses the plane.
  function floorViewAngle(center: THREE.Vector3): number | null {
    deps.activeCamera().getWorldPosition(_fgCamPos);
    _fgN.copy(_fgCamPos).sub(center);
    if (_fgN.lengthSq() < 1e-9) return null;
    _fgN.normalize();
    _fgR.crossVectors(_fgUp, _fgN);
    if (_fgR.lengthSq() < 1e-6) _fgR.set(1, 0, 0);
    else _fgR.normalize();
    _fgU.crossVectors(_fgN, _fgR).normalize();
    _ray.setFromCamera(desktopMouse.ndc, deps.activeCamera());
    _fgPlane.setFromNormalAndCoplanarPoint(_fgN, center);
    if (!_ray.ray.intersectPlane(_fgPlane, _fgHit)) return null;
    _fgHit.sub(center);
    return Math.atan2(_fgHit.dot(_fgU), _fgHit.dot(_fgR));
  }

  function startFloorGizmoDrag(hit: GizmoHit): void {
    const places = gizmoPlaces();
    if (places.length === 0 || !floorGizmoTarget()) return; // sets _fgCenter from the SELECTION
    const center = _fgCenter.clone();
    // A MOVE (translate / free) carries rigidly-linked models along so the locked
    // measurement between them is preserved; a ROTATE turns only the selection
    // (the gizmo still pivots on the selection's centre, set above).
    const moved = hit.kind === "rotate" ? places : expandRigidGroup(places);
    const starts: { id: number; pos: THREE.Vector3; quat: THREE.Quaternion }[] = [];
    const placeSet = new Set<number>();
    let bMinX = Infinity;
    let bMaxX = -Infinity;
    let bMinZ = Infinity;
    let bMaxZ = -Infinity;
    for (const pid of moved) {
      const g = placementSystem.groupOf(pid);
      if (!g) continue;
      starts.push({ id: pid, pos: g.position.clone(), quat: g.quaternion.clone() });
      placeSet.add(pid);
      const a = placementSystem.aabbOf(pid);
      if (a) {
        bMinX = Math.min(bMinX, a.minX);
        bMaxX = Math.max(bMaxX, a.maxX);
        bMinZ = Math.min(bMinZ, a.minZ);
        bMaxZ = Math.max(bMaxZ, a.maxZ);
      }
    }
    if (starts.length === 0) return;
    const bbox =
      bMinX <= bMaxX ? { minX: bMinX, maxX: bMaxX, minZ: bMinZ, maxZ: bMaxZ } : null;
    // Walls only ride along for a marquee group (translate/free); single-object
    // selection has none.
    const wallVids =
      deps.marquee.places().size > 0
        ? [...wallSystem.vertexIdsOf(deps.marquee.walls())]
        : [];
    const base = {
      axis: hit.axis,
      grabParam: 0,
      starts,
      placeSet,
      bbox,
      wallVids,
      applied: new THREE.Vector3(),
      grabPoint: new THREE.Vector3(),
      freeNormalAxis: 1,
      center,
      prevAngle: 0,
      accumAngle: 0,
      signFlip: 1,
      refDir: new THREE.Vector3(1, 0, 0),
    };
    if (hit.kind === "translate") {
      const t = closestParamOnAxis(
        center,
        _fgAxes[hit.axis],
        _ray.ray.origin,
        _ray.ray.direction,
      );
      fgDrag = { ...base, mode: "translate", grabParam: t ?? 0 };
    } else if (hit.kind === "free") {
      // Drag in the plane perpendicular to the view axis (same plane as the two
      // visible translation arrows); 3D keeps the floor plane.
      const normalAxis = deps.freeMoveAxis();
      const grab = new THREE.Vector3();
      _fgPlane.setFromNormalAndCoplanarPoint(_fgAxes[normalAxis], center);
      if (!_ray.ray.intersectPlane(_fgPlane, grab)) grab.copy(center);
      fgDrag = { ...base, mode: "free", freeNormalAxis: normalAxis, grabPoint: grab };
    } else {
      // Rotate about ANY axis (X/Y/Z) via the view-plane angle.
      const a0 = floorViewAngle(center); // also fills _fgN for signFlip
      const sf = Math.sign(_fgAxes[hit.axis].dot(_fgN));
      // Grab reference direction for the protractor: the click ray's hit on the
      // ring plane (normal = axis), projected into that plane.
      const axisW = _fgAxes[hit.axis];
      const refDir = new THREE.Vector3();
      _fgPlane.setFromNormalAndCoplanarPoint(axisW, center);
      if (_ray.ray.intersectPlane(_fgPlane, refDir)) {
        refDir.sub(center).addScaledVector(axisW, -refDir.dot(axisW));
      }
      if (refDir.lengthSq() < 1e-8) {
        refDir.set(Math.abs(axisW.x) > 0.9 ? 0 : 1, 0, Math.abs(axisW.x) > 0.9 ? 1 : 0);
        refDir.addScaledVector(axisW, -refDir.dot(axisW));
      }
      refDir.normalize();
      fgDrag = {
        ...base,
        mode: "rotate",
        prevAngle: a0 ?? 0,
        signFlip: sf === 0 ? 1 : sf,
        refDir,
      };
    }
  }

  // Apply a world translation `delta` to every selected placement (clamped to
  // the floor) and ride the selected wall vertices along on X/Z.
  function applyGroupTranslate(delta: THREE.Vector3, snapX: boolean, snapZ: boolean): void {
    if (!fgDrag) return;
    // Snap the WHOLE selection's combined footprint flush to walls / other
    // objects (single object or group alike). The same offset shifts every
    // member + any attached walls, so the group stays rigid and lands aligned.
    let sdx = 0;
    let sdz = 0;
    if (fgDrag.bbox && (snapX || snapZ)) {
      const s = snapGroup(fgDrag.bbox, delta.x, delta.z, fgDrag.placeSet, snapX, snapZ);
      sdx = s.dx;
      sdz = s.dz;
    }
    const totalX = delta.x + sdx;
    const totalZ = delta.z + sdz;
    for (const s of fgDrag.starts) {
      const g = placementSystem.groupOf(s.id);
      if (!g) continue;
      let ny = s.pos.y + delta.y;
      if (ny < 0) ny = 0; // rest on/above the floor
      g.position.set(s.pos.x + totalX, ny, s.pos.z + totalZ);
    }
    if (fgDrag.wallVids.length > 0) {
      wallSystem.translateVerts(
        fgDrag.wallVids,
        totalX - fgDrag.applied.x,
        totalZ - fgDrag.applied.z,
      );
    }
    fgDrag.applied.set(totalX, delta.y, totalZ);
  }

  function applyFloorGizmoDrag(): void {
    if (!fgDrag) return;
    if (fgDrag.starts.length === 0) {
      fgDrag = null;
      return;
    }
    if (fgDrag.mode === "translate") {
      _ray.setFromCamera(desktopMouse.ndc, deps.activeCamera());
      const t = closestParamOnAxis(
        fgDrag.center,
        _fgAxes[fgDrag.axis],
        _ray.ray.origin,
        _ray.ray.direction,
      );
      if (t === null) return;
      _fgMove.copy(_fgAxes[fgDrag.axis]).multiplyScalar(t - fgDrag.grabParam);
      // Snap only the dragged floor axis (Y never snaps).
      applyGroupTranslate(_fgMove, fgDrag.axis === 0, fgDrag.axis === 2);
    } else if (fgDrag.mode === "free") {
      _ray.setFromCamera(desktopMouse.ndc, deps.activeCamera());
      _fgPlane.setFromNormalAndCoplanarPoint(_fgAxes[fgDrag.freeNormalAxis], fgDrag.center);
      if (!_ray.ray.intersectPlane(_fgPlane, _fgFreeHit)) return;
      const na = fgDrag.freeNormalAxis;
      // In-plane delta from the grab point (the view-axis component stays 0).
      _fgMove.set(
        na === 0 ? 0 : _fgFreeHit.x - fgDrag.grabPoint.x,
        na === 1 ? 0 : _fgFreeHit.y - fgDrag.grabPoint.y,
        na === 2 ? 0 : _fgFreeHit.z - fgDrag.grabPoint.z,
      );
      // Snap the in-plane floor axes (the view/normal axis is held fixed).
      applyGroupTranslate(_fgMove, na !== 0, na !== 2);
    } else {
      const a = floorViewAngle(fgDrag.center); // fills _fgR/_fgU/_fgHit (cursor)
      if (a === null) return;
      fgDrag.accumAngle += wrapPiF(a - fgDrag.prevAngle);
      fgDrag.prevAngle = a;
      // Snap the rotation amount to 15° steps (Shift frees it).
      let theta = fgDrag.accumAngle * fgDrag.signFlip;
      if (!deps.isShiftHeld()) theta = Math.round(theta / deps.angleSnapRad) * deps.angleSnapRad;
      _fgDelta.setFromAxisAngle(_fgAxes[fgDrag.axis], theta);
      // Rotate every selected placement about the shared centre (orbit its
      // position + spin its orientation). Walls keep their shape.
      for (const s of fgDrag.starts) {
        const g = placementSystem.groupOf(s.id);
        if (!g) continue;
        g.quaternion.copy(_fgDelta).multiply(s.quat);
        _fgRel.copy(s.pos).sub(fgDrag.center).applyQuaternion(_fgDelta);
        g.position.copy(fgDrag.center).add(_fgRel);
        if (g.position.y < 0) g.position.y = 0;
      }
      // Protractor: a wedge in the RING plane sweeping the grab reference by the
      // applied (snapped) rotation, a degree readout, and a dashed line out to
      // the cursor (also in the ring plane).
      const axisW = _fgAxes[fgDrag.axis];
      const arcR = RING_RADIUS * gizmoScreenScale(fgDrag.center);
      _fgPlane.setFromNormalAndCoplanarPoint(axisW, fgDrag.center);
      if (!_ray.ray.intersectPlane(_fgPlane, _fgCursorWorld)) {
        _fgCursorWorld.copy(fgDrag.center).addScaledVector(fgDrag.refDir, arcR);
      }
      floorProtractor.update(
        fgDrag.center,
        axisW,
        fgDrag.refDir,
        theta,
        arcR,
        _fgCursorWorld,
        (theta * 180) / Math.PI,
        AXIS_COLORS[fgDrag.axis],
      );
    }
    deps.refreshSelectionUI();
  }

  // ── Select-chain hooks ────────────────────────────────────────────────────
  function gizmoUnderCursor(): boolean {
    if (gizmoPlaces().length === 0) return false;
    _ray.setFromCamera(desktopMouse.ndc, deps.activeCamera());
    return floorGizmo.raycast(_ray) !== null;
  }
  // Press on a marquee-selected item (no modifier) → drag the whole group in
  // the view plane (floor X/Z aerial; X/Y or Y/Z from a side elevation).
  function beginGroupDrag(): boolean {
    if (!deps.marquee.hasSelection() || !deps.marquee.pressHitsSelection()) return false;
    const gNormal = deps.freeMoveAxis();
    const grab = viewPlanePointAtCursor(gNormal);
    if (!grab) return false;
    groupDrag = {
      grab: grab.clone(),
      normalAxis: gNormal,
      applied: new THREE.Vector3(),
      minPlaceY: placementSystem.minY(deps.marquee.places()),
    };
    return true;
  }
  // Press on a gizmo handle — but an object NOT in the selection whose surface
  // sits IN FRONT of the grabbed handle should win, so the gizmo's fat
  // hit-proxies can't block clicking another object within its footprint. Any
  // accepted handle consumes the click (start may no-op for an unsupported
  // handle) so it never falls through to dragging the placement — that was the
  // "jumps to cursor" bug.
  function pressGizmo(): boolean {
    const gizPlaces = gizmoPlaces();
    if (gizPlaces.length === 0) return false;
    _ray.setFromCamera(desktopMouse.ndc, deps.activeCamera());
    const gizmoHit = floorGizmo.raycast(_ray);
    if (!gizmoHit) return false;
    const ph = placementSystem.pickHit(_ray);
    const blocked =
      ph !== null && !gizPlaces.includes(ph.id) && ph.distance < gizmoHit.distance;
    if (blocked) return false;
    startFloorGizmoDrag(gizmoHit);
    return true;
  }
  function dragGizmo(): boolean {
    if (!fgDrag || !desktopMouse.lmb) return false;
    applyFloorGizmoDrag();
    return true;
  }
  // Translate the whole marquee selection by the grid-snapped total delta in
  // the view plane (Shift = free). The view-axis component is ~0 by
  // construction (grab + cursor share the plane), so walls — which take only
  // (dx,dz) and ignore Y — stay floor-bound automatically, while placements
  // also move vertically from a side elevation.
  function dragGroup(): boolean {
    if (groupDrag === null || !desktopMouse.lmb) return false;
    const cur = viewPlanePointAtCursor(groupDrag.normalAxis);
    if (cur) {
      let tx = cur.x - groupDrag.grab.x;
      let ty = cur.y - groupDrag.grab.y;
      let tz = cur.z - groupDrag.grab.z;
      if (!deps.isShiftHeld()) {
        tx = deps.snapToGrid(tx);
        ty = deps.snapToGrid(ty);
        tz = deps.snapToGrid(tz);
      }
      // Cap the group's descent so the lowest placement stops at the floor and
      // the whole selection halts together (no per-item clamp desync). Upward
      // is unbounded; walls ignore Y so they're unaffected.
      if (isFinite(groupDrag.minPlaceY)) ty = Math.max(ty, -groupDrag.minPlaceY);
      const dx = tx - groupDrag.applied.x;
      const dy = ty - groupDrag.applied.y;
      const dz = tz - groupDrag.applied.z;
      if (dx !== 0 || dy !== 0 || dz !== 0) {
        wallSystem.translateVerts(wallSystem.vertexIdsOf(deps.marquee.walls()), dx, dz);
        placementSystem.translate(deps.marquee.places(), dx, dy, dz);
        groupDrag.applied.set(tx, ty, tz);
      }
    }
    return true;
  }

  // Shape the rotate rings for the 3D layout view (called with _fgCenter already
  // set). Free orbit → all three rings are half-arcs facing the camera, so the
  // gizmo reads less busy (you only ever grab the near half). When the view is
  // aligned down a world axis (corner-gizmo click / square-on) → that ring's
  // half-arc becomes a full circle and the two now edge-on rings hide, since you
  // can't grab them accurately from there anyway. Visibility resets to all-shown
  // each frame via showAllHandles() before this runs.
  function shapeRotationRings3D(): void {
    deps.activeCamera().getWorldPosition(_fgCamPos);
    _fgRingDir.copy(_fgCamPos).sub(_fgCenter);
    if (_fgRingDir.lengthSq() < 1e-9) {
      for (let a = 0; a < 3; a++) floorGizmo.setRingFull(a);
      return;
    }
    _fgRingDir.normalize();
    // Aligned when the view direction is within ~6° of a world axis (an explicit
    // corner-gizmo axis click lands exactly on one).
    let aligned = -1;
    for (let a = 0; a < 3; a++) {
      if (Math.abs(_fgRingDir.getComponent(a)) > 0.995) aligned = a;
    }
    for (let a = 0; a < 3; a++) {
      if (aligned === -1) floorGizmo.setRingArc(a, _fgRingDir);
      else if (a === aligned) floorGizmo.setRingFull(a);
      else floorGizmo.setHandleVisible("rotate", a, false);
    }
  }

  // Per-frame: position/scale the gizmo on the selected placement + highlight.
  const _outlineGroups: THREE.Object3D[] = [];
  // Hidden models under the cursor this frame — rendered as a translucent ghost
  // (so you can right-click → "Show model"). Empty most frames.
  const _ghostIds: number[] = [];
  function updateGizmo(): void {
    const selecting = deps.activeTool() === "select";
    const sid = selecting ? placementSystem.selectedId() : null;
    // White-edge highlight every selected placement: the marquee group + the
    // single gizmo selection (deduped).
    _outlineGroups.length = 0;
    _ghostIds.length = 0;
    if (selecting) {
      for (const pid of deps.marquee.places()) {
        const g = placementSystem.groupOf(pid);
        if (g) _outlineGroups.push(g);
      }
      if (sid !== null) {
        const g = placementSystem.groupOf(sid);
        if (g && !_outlineGroups.includes(g)) _outlineGroups.push(g);
      }
      // Hover: white-edge the model under the cursor too (its whole group, so
      // you see what a click will select). Skip while dragging / rubber-banding.
      if (
        desktopMouse.overCanvas &&
        !desktopMouse.camDragging &&
        !fgDrag &&
        !groupDrag &&
        !deps.marquee.bandActive() &&
        !desktopMouse.lmb
      ) {
        _ray.setFromCamera(desktopMouse.ndc, deps.activeCamera());
        // includeHidden so a hidden model still registers under the cursor — it
        // ghosts (translucent) instead of getting white edges.
        const hit = placementSystem.pickHit(_ray, true);
        // Don't hover-highlight a model on the far side of a wall/doorway when
        // looking at it head-on in the side elevation.
        if (hit && hit.distance <= deps.occluderDist(_ray)) {
          for (const sib of placementSystem.groupSiblings(hit.id)) {
            // A hidden model also ghosts translucent; either way it gets the
            // white-edge hover outline (the ghost keeps its edge lines visible).
            if (placementSystem.isHidden(sib)) _ghostIds.push(sib);
            const g = placementSystem.groupOf(sib);
            if (g && !_outlineGroups.includes(g)) _outlineGroups.push(g);
          }
        }
      }
      // While the right-click menu is open, keep its target(s) highlighted (the
      // cursor has left the model for the menu, so hover alone wouldn't). A hidden
      // target stays ghosted so its "Show model" target doesn't blink out.
      for (const id of deps.contextHighlightIds()) {
        if (placementSystem.isHidden(id)) _ghostIds.push(id);
        const g = placementSystem.groupOf(id);
        if (g && !_outlineGroups.includes(g)) _outlineGroups.push(g);
      }
    }
    // Apply (or clear) the hover ghost for hidden models. Outside the `selecting`
    // block so leaving Select / moving off the model snaps ghosts back to hidden.
    placementSystem.setGhosts(_ghostIds);
    deps.placementOutline.setTargets(_outlineGroups);
    // The gizmo shows for a single selection OR a marquee group, centred between
    // the selected objects — but not while still rubber-banding a marquee.
    if (!selecting || deps.marquee.bandActive() || gizmoPlaces().length === 0) {
      floorGizmo.hide();
      floorProtractor.hide();
      return;
    }
    // During a rotate the bbox-centroid drifts as objects turn, so hold the
    // captured pivot; otherwise track the live centroid.
    if (fgDrag && fgDrag.mode === "rotate") _fgCenter.copy(fgDrag.center);
    else floorGizmoTarget(); // sets _fgCenter
    floorGizmo.setTarget(_fgCenter, gizmoScreenScale(_fgCenter));
    if (fgDrag && fgDrag.mode === "rotate") {
      floorGizmo.isolateHandle("rotate", fgDrag.axis);
      floorGizmo.setRingFull(fgDrag.axis); // dragged ring: full-circle guide
    } else {
      floorGizmo.showAllHandles();
      floorProtractor.hide(); // only shown mid rotate-drag
      if (uiState.floorView === "3d") shapeRotationRings3D();
    }
    // In the 2D plan view, show only the handles that aren't degenerate from
    // this axis-aligned angle: rotate AROUND the view axis (its ring faces the
    // camera) plus translate along the two in-screen-plane axes. The along-view
    // translate arrow (points at the camera) and the two edge-on tilt rings are
    // hidden — and dropped from the raycast, so they can't be grabbed. (Also
    // covers the side elevations: looking down Z shows the Z ring + X/Y arrows.)
    if (uiState.floorView === "2d") {
      const va = deps.planViewAxis();
      for (let a = 0; a < 3; a++) {
        floorGizmo.setHandleVisible("translate", a, a !== va);
        floorGizmo.setHandleVisible("rotate", a, a === va);
      }
      floorGizmo.setRingFull(va); // the single plan ring is a full circle
      // The center "free move" handle stays — it drags in the plane of the two
      // visible arrows (perpendicular to the view axis; see freeMoveAxis).
    }
    const active: GizmoHit | null = fgDrag
      ? { kind: fgDrag.mode, axis: fgDrag.axis, distance: 0 }
      : null;
    let hover: GizmoHit | null = null;
    if (!fgDrag && desktopMouse.overCanvas && !desktopMouse.camDragging) {
      _ray.setFromCamera(desktopMouse.ndc, deps.activeCamera());
      hover = floorGizmo.raycast(_ray);
    }
    floorGizmo.applyHighlight(hover, active);
  }

  return {
    floorGizmo,
    floorProtractor,
    gizmoPlaces,
    gizmoUnderCursor,
    beginGroupDrag,
    pressGizmo,
    dragGizmo,
    dragGroup,
    gizmoDragArmed: () => fgDrag !== null,
    groupDragArmed: () => groupDrag !== null,
    clearGestures: () => {
      fgDrag = null;
      groupDrag = null;
    },
    updateGizmo,
  };
}
